// ---------------------------------------------------------------
// One place to send email. If a backend send-endpoint is configured (config
// EMAIL_ENDPOINT), emails go out automatically. Otherwise it opens the user's own
// email app with everything pre-filled (they hit send). Same call site either
// way, so wiring the backend later needs no UI changes.
//   Used for: rental invoices, receipts, contract-to-sign, password resets.
// ---------------------------------------------------------------
import { EMAIL_ENDPOINT, EMAILJS_SERVICE, EMAILJS_TEMPLATE, EMAILJS_PUBLIC_KEY } from './config';

const emailjsReady = () => !!(EMAILJS_SERVICE && EMAILJS_TEMPLATE && EMAILJS_PUBLIC_KEY);
export const emailBackendReady = () => !!EMAIL_ENDPOINT || emailjsReady();

async function sendViaEmailJS({ to, subject, body, cc }) {
  const r = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      service_id: EMAILJS_SERVICE,
      template_id: EMAILJS_TEMPLATE,
      user_id: EMAILJS_PUBLIC_KEY,
      template_params: { to_email: to, subject, message: body, cc: cc || '' },
    }),
  });
  return r.ok;
}

// Returns 'sent' (auto), 'compose' (opened mail app), or 'cancel'.
export async function sendEmail({ to, subject, body, cc }) {
  if (emailjsReady()) {
    try { if (await sendViaEmailJS({ to, subject, body, cc })) return 'sent'; } catch { /* fall through */ }
  }
  if (EMAIL_ENDPOINT) {
    try {
      const r = await fetch(EMAIL_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to, subject, body, cc }),
      });
      if (r.ok) return 'sent';
    } catch { /* fall back to the mail app */ }
  }
  const url = `mailto:${encodeURIComponent(to || '')}?subject=${encodeURIComponent(subject || '')}`
    + `&body=${encodeURIComponent(body || '')}${cc ? `&cc=${encodeURIComponent(cc)}` : ''}`;
  try { window.open(url); return 'compose'; } catch { return 'cancel'; }
}

// ---- ready-made templates ----
export function invoiceEmail({ renter, vehicle, dates, mileage, note, chargeLines, amount, link }) {
  return {
    subject: `Rental invoice — ${amount} due`,
    body: `Hi ${renter || ''},

Here is your rental invoice.

Vehicle: ${vehicle}
Rental period: ${dates}
Mileage: ${mileage}${note ? `\n\nNote: ${note}` : ''}

Charges:
${chargeLines}

Amount due: ${amount}
${link ? `\nPay securely here: ${link}\n` : ''}
Thank you.`,
  };
}

export function receiptEmail({ renter, vehicle, amount, method, dateStr, balance }) {
  return {
    subject: `Payment receipt — ${amount}`,
    body: `Hi ${renter || ''},

Thank you — we've received your payment.

Vehicle: ${vehicle}
Amount paid: ${amount}${method ? ` (${method})` : ''}
Date: ${dateStr}
${balance ? `Remaining balance: ${balance}` : 'Paid in full — no balance due.'}

Thank you for renting with us.`,
  };
}

export function contractEmail({ renter, vehicle, contractText, signLink }) {
  return {
    subject: `Rental agreement for ${vehicle} — please review & sign`,
    body: `Hi ${renter || ''},

Please review the rental agreement for ${vehicle} below.
${signLink ? `\nReview and sign it here: ${signLink}\n` : '\n(Reply to this email or sign in person at pickup.)\n'}
--------------------------------------------------
${contractText}
--------------------------------------------------

Thank you.`,
  };
}

// Password reset. This TRULY needs the backend to generate a secure reset token
// and verify it — until EMAIL_ENDPOINT + backend are live it can only send a
// request to support. Returns false when it can't do a real reset.
export async function sendPasswordReset({ to, resetLink }) {
  if (!emailBackendReady()) return false;
  const subject = 'Reset your TagAlong password';
  const body = `We received a request to reset your password.

Reset it here: ${resetLink}

If you didn't request this, you can ignore this email.`;
  const res = await sendEmail({ to, subject, body });
  return res === 'sent';
}
